import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RemindersAPI } from "../api/reminders";
import type { ListRemindersOptions, Reminder } from "../types";

export const remindersKeys = {
  all: ["reminders"] as const,
  list: (clientId: string, options?: ListRemindersOptions) =>
    [...remindersKeys.all, "list", clientId, options] as const,
};

/**
 * Whether the tenant switched the scheduled-reminders module on. Everything that
 * displays reminders is gated on it: with the module off there is nothing to show
 * and no reason to query for it.
 */
export function useRemindersEnabled() {
  const { data: availableTools = [] } = useAvailableTools();
  const basicTools = availableTools.find((t) => t.tool.name === "basic_tools");

  if (!basicTools?.clientTool?.enabled) return false;

  const channels = basicTools.clientTool.configuration?.reminder_channels;
  return !!channels && channels !== "disabled";
}

export function useReminders(
  options?: ListRemindersOptions & { enabled?: boolean },
) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const { enabled = true, ...listOptions } = options || {};

  return useQuery<Reminder[]>({
    queryKey: remindersKeys.list(clientId, listOptions),
    queryFn: () => RemindersAPI.list(clientId, listOptions),
    enabled: !!clientId && enabled,
    staleTime: 30 * 1000,
  });
}

export function useCancelReminder() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reminderId: string) =>
      RemindersAPI.cancel(clientId, reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: remindersKeys.all });
    },
  });
}
